import { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Row, message } from "antd";
import {
  DashboardOutlined,
  DatabaseOutlined,
  ThunderboltOutlined,
  ClockCircleOutlined,
  WifiOutlined,
} from "@ant-design/icons";
import { api, type Health } from "../api";
import { useSSE } from "../hooks/useSSE";
import LiveChart from "../components/LiveChart";
import TopTalkersTable from "../components/TopTalkersTable";
import TopASNTable from "../components/TopASNTable";
import DashboardGrid, { type WidgetDef } from "../components/DashboardGrid";
import { useGeoSettings } from "../contexts/GeoSettingsContext";

// IXP-specific widget IDs — filtered out when IXP collection is disabled.
// (The NIX ASN widget is gated by nix_enabled below.)
const IXP_WIDGET_IDS = new Set([
  "top-ixp-bps-in",
  "top-ixp-bps-out",
  "top-asn-ixp-in",
  "top-asn-ixp-out",
]);
const NIX_WIDGET_IDS = new Set(["top-asn-nix-in", "top-asn-nix-out"]);

// ── widget definitions (full set) ─────────────────────────────────────────────

const ALL_WIDGETS: WidgetDef[] = [
  {
    id: "chart-global",
    defaultLayout: { x: 0, y: 0, w: 12, h: 10 },
    minW: 4,
    minH: 5,
    title: "Bits/s :: Global",
    color: "#c0392b",
    icon: "≈",
    children: <LiveChart seconds={600} refreshMs={5000} height={200} />,
  },
  {
    id: "top-bps-in",
    defaultLayout: { x: 0, y: 10, w: 6, h: 11 },
    minW: 3,
    minH: 4,
    title: "Top Inbound Talkers :: Bits/s :: Live",
    color: "#4472C4",
    icon: "↓",
    children: <TopTalkersTable metric="bps" dir="in" color="#4472C4" />,
  },
  {
    id: "top-bps-out",
    defaultLayout: { x: 6, y: 10, w: 6, h: 11 },
    minW: 3,
    minH: 4,
    title: "Top Outbound Talkers :: Bits/s :: Live",
    color: "#ED7D31",
    icon: "↑",
    children: <TopTalkersTable metric="bps" dir="out" color="#ED7D31" />,
  },
  {
    id: "top-pps-in",
    defaultLayout: { x: 0, y: 21, w: 6, h: 11 },
    minW: 3,
    minH: 4,
    title: "Top Packets In :: Pkts/s :: Live",
    color: "#70AD47",
    icon: "↓",
    children: <TopTalkersTable metric="pps" dir="in" color="#70AD47" />,
  },
  {
    id: "top-pps-out",
    defaultLayout: { x: 6, y: 21, w: 6, h: 11 },
    minW: 3,
    minH: 4,
    title: "Top Packets Out :: Pkts/s :: Live",
    color: "#FF4D4F",
    icon: "↑",
    children: <TopTalkersTable metric="pps" dir="out" color="#FF4D4F" />,
  },
  {
    id: "top-ixp-bps-in",
    defaultLayout: { x: 0, y: 32, w: 6, h: 11 },
    minW: 3,
    minH: 4,
    title: "Top IXP Inbound :: Bits/s :: Live",
    color: "#2e86de",
    icon: "↓",
    children: <TopTalkersTable metric="bps" dir="in" color="#2e86de" geo="ixp" />,
  },
  {
    id: "top-ixp-bps-out",
    defaultLayout: { x: 6, y: 32, w: 6, h: 11 },
    minW: 3,
    minH: 4,
    title: "Top IXP Outbound :: Bits/s :: Live",
    color: "#e67e22",
    icon: "↑",
    children: <TopTalkersTable metric="bps" dir="out" color="#e67e22" geo="ixp" />,
  },
  // ── Top ASN widgets (powered by GeoLite2-ASN.mmdb) ──────────────────────
  {
    id: "top-asn-ixp-in",
    defaultLayout: { x: 0, y: 43, w: 6, h: 11 },
    minW: 3,
    minH: 4,
    title: "Top ASN :: IXP Inbound :: Live",
    color: "#8e44ad",
    icon: "↓",
    children: <TopASNTable geo="ixp" dir="in" metric="bps" color="#8e44ad" />,
  },
  {
    id: "top-asn-ixp-out",
    defaultLayout: { x: 6, y: 43, w: 6, h: 11 },
    minW: 3,
    minH: 4,
    title: "Top ASN :: IXP Outbound :: Live",
    color: "#16a085",
    icon: "↑",
    children: <TopASNTable geo="ixp" dir="out" metric="bps" color="#16a085" />,
  },
  {
    id: "top-asn-nix-in",
    defaultLayout: { x: 0, y: 54, w: 6, h: 11 },
    minW: 3,
    minH: 4,
    title: "Top ASN :: NIX Inbound :: Live",
    color: "#2980b9",
    icon: "↓",
    children: <TopASNTable geo="nix" dir="in" metric="bps" color="#2980b9" />,
  },
  {
    id: "top-asn-nix-out",
    defaultLayout: { x: 6, y: 54, w: 6, h: 11 },
    minW: 3,
    minH: 4,
    title: "Top ASN :: NIX Outbound :: Live",
    color: "#d35400",
    icon: "↑",
    children: <TopASNTable geo="nix" dir="out" metric="bps" color="#d35400" />,
  },
];

// ── Stat card component ─────────────────────────────────────────────────────

type StatTone = "ok" | "warn" | "err" | "neutral";

function StatCard({
  label,
  value,
  icon,
  tone,
  live,
  sm,
}: {
  label: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  tone: StatTone;
  live?: boolean;
  sm?: boolean;
}) {
  const toneClass =
    tone === "ok" ? "stat-ok" : tone === "warn" ? "stat-warn" : tone === "err" ? "stat-err" : "";
  const dotClass = live ? "live" : tone === "err" ? "err" : "";
  return (
    <div className={`stat-card ${toneClass}`}>
      <div className="stat-icon">{icon}</div>
      <div className="stat-body">
        <div className="stat-label">
          <span className={`stat-dot ${dotClass}`} />
          {label}
        </div>
        <div className={`stat-value${sm ? " stat-value-sm" : ""}`}>{value}</div>
      </div>
    </div>
  );
}

// ── main page ─────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const [health, setHealth] = useState<Health | null>(null);
  const [loading, setLoading] = useState(true);
  const { tick, connected } = useSSE("/api/v1/events/stream");
  const { settings: geoSettings } = useGeoSettings();

  // Gate IXP-only tiles when IXP collection is disabled, and NIX-ASN tiles
  // when NIX collection is disabled. (NIX IP tiles stay always on because
  // they include intra-zone traffic that isn't classified by Country.mmdb.)
  const widgets = useMemo(
    () =>
      ALL_WIDGETS.filter((w) => {
        if (IXP_WIDGET_IDS.has(w.id) && !geoSettings.ixp_enabled) return false;
        if (NIX_WIDGET_IDS.has(w.id) && !geoSettings.nix_enabled) return false;
        return true;
      }),
    [geoSettings.ixp_enabled, geoSettings.nix_enabled]
  );

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const h = await api.health();
      setHealth(h);
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const lastTickAgo = tick
    ? `${Math.max(0, Math.floor(Date.now() / 1000 - tick.ts))}s ago`
    : "—";

  void loading; // suppress unused warning

  const apiOk = health?.status === "ok";
  const dbOk  = !!health?.db;

  return (
    <>
      {/* Page header */}
      <div className="page-header-band">
        <div className="ph-left">
          <div className="ph-icon">
            <DashboardOutlined />
          </div>
          <div>
            <div className="ph-title">Sensors Dashboard</div>
            <div className="ph-subtitle">Real-time network traffic monitoring</div>
          </div>
        </div>
      </div>

      {/* Status row */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            label="API"
            value={apiOk ? "Operational" : health?.status ?? "…"}
            icon={<ThunderboltOutlined />}
            tone={apiOk ? "ok" : "err"}
            live={apiOk}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            label="Uptime"
            value={health?.uptime ?? "…"}
            icon={<ClockCircleOutlined />}
            tone="neutral"
            sm
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            label="Database"
            value={dbOk ? "Connected" : "Down"}
            icon={<DatabaseOutlined />}
            tone={dbOk ? "ok" : "err"}
            live={dbOk}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            label="Live stream"
            value={connected ? lastTickAgo : "Disconnected"}
            icon={<WifiOutlined />}
            tone={connected ? "ok" : "err"}
            live={connected}
            sm
          />
        </Col>
      </Row>

      {/* Draggable / resizable widget grid */}
      <DashboardGrid widgets={widgets} />
    </>
  );
}
